import React, { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { ArrowLeft, Upload, X, Loader2 } from 'lucide-react';
import useApi from "../../api_service/useApi";
import { toast } from "react-toastify";

interface CreateSchoolFormData {
  firstName: string;
  lastName: string;
  schoolName: string;
  email: string;
  city?: string;
  numberOfStudents?: number;
  billingCycle?: 'yearly' | 'monthly';
  schoolLogoUrl?: string;
}

type LayoutContextType = {
  setSchoolAdminPageName: React.Dispatch<React.SetStateAction<string>>;
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>;
  setMenu: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>;
  setIsAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
};

const CreateSchoolPage: React.FC = () => {
  const navigate = useNavigate();
  const path = window.location.pathname;

  const { loading, post } = useApi();
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const {
    setSchoolAdminPageName,
    setShowInput,
    setMenu,
    setIsAffiliate,
  } = useOutletContext<LayoutContextType>();

  const [formData, setFormData] = useState<CreateSchoolFormData>({
    firstName: '',
    lastName: '',
    schoolName: '',
    email: '',
    city: '',
    numberOfStudents: undefined,
    billingCycle: undefined,
    schoolLogoUrl: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreateSchoolFormData, string>>>({});

  React.useEffect(() => {
    setSchoolAdminPageName("Create School");
    setShowInput(false);
    setMenu(undefined);
    setIsAffiliate(false);
  }, [path]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateSchoolFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.schoolName.trim()) {
      newErrors.schoolName = 'School name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'numberOfStudents' ? (value ? parseInt(value) : undefined) : value
    }));
    
    if (errors[name as keyof CreateSchoolFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const uploadToCloudinary = async (file: File): Promise<string> => {
    const token = localStorage.getItem('token');
    const apiBaseUrl = import.meta.env.VITE_NEXT_PUBLIC_GMIND_BASEURL || 'https://staging.golive.gmind.ai/v1/admin';
    const uploadUrl = `${apiBaseUrl.replace('/v1/admin', '')}/v1/gmind/chat-image`;

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      if (data.statusCode === 1 && data.url) {
        return data.url;
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    try {
      const logoUrl = await uploadToCloudinary(file);
      setFormData(prev => ({ ...prev, schoolLogoUrl: logoUrl }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo. Please try again.');
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setFormData(prev => ({ ...prev, schoolLogoUrl: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const token = localStorage.getItem('token');

    const payload = new FormData();
    payload.append('firstName', formData.firstName);
    payload.append('lastName', formData.lastName);
    payload.append('schoolName', formData.schoolName);
    payload.append('email', formData.email);
    if (formData.city) payload.append('city', formData.city);
    if (formData.numberOfStudents) payload.append('numberOfStudents', formData.numberOfStudents.toString());
    if (formData.billingCycle) payload.append('billingCycle', formData.billingCycle);
    if (formData.schoolLogoUrl) payload.append('schoolLogoUrl', formData.schoolLogoUrl);

    await post<any, any>(
      '/third_party/create_school_app',
      payload,
      { Authorization: `Bearer ${token}` },
      {
        onSuccess: (response) => {
          toast.success('School created successfully!');
          navigate('/home/school-ai');
        },
        onError: (err) => {
          toast.error(err?.errorMsg || 'Failed to create school. Please try again.');
        }
      }
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home/school-ai')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Schools
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Create New School</h1>
          <p className="text-gray-600 mt-1">Fill in the details to create a new school AI application</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-8">
          {/* Logo Upload */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              School Logo
            </label>
            {logoPreview ? (
              <div className="relative inline-block">
                <img
                  src={logoPreview}
                  alt="School logo preview"
                  className="w-32 h-32 rounded-lg object-cover border-2 border-gray-200"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                  disabled={uploadingLogo}
                />
                <label
                  htmlFor="logo-upload"
                  className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#E55109] transition-colors bg-gray-50"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-10 h-10 text-[#E55109] animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload school logo</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>

          {/* Personal Information */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>
          </div>

          {/* School Information */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">School Information</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  School Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none ${
                    errors.schoolName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter school name"
                />
                {errors.schoolName && (
                  <p className="text-red-500 text-sm mt-1">{errors.schoolName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="school@example.com"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none"
                    placeholder="Enter city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Students
                  </label>
                  <input
                    type="number"
                    name="numberOfStudents"
                    value={formData.numberOfStudents || ''}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none"
                    placeholder="Enter number of students"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Billing Cycle
                </label>
                <select
                  name="billingCycle"
                  value={formData.billingCycle || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none"
                >
                  <option value="">Select billing cycle</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/home/school-ai')}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingLogo}
              className="flex-1 px-6 py-3 bg-[#E55109] text-white rounded-lg hover:bg-[#d14808] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create School'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSchoolPage;
